import React from 'react';

/**
 * BrainPulseLoader - compact, inline variant of BrainSyncLoader.
 *
 * Designed for placeholder slots inside cards / detail blocks, where the
 * full-screen loader would be too heavy. Same visual vocabulary:
 *   - dashed rotating sync ring
 *   - dark rounded-tile brain glyph
 *   - soft emerald pulse glow
 *
 * Sizes (size prop, in px): default 48 (card overlay), 64 (detail block).
 * Pure CSS animations - no Motion/Framer dependency.
 */
export default function BrainPulseLoader({ size = 48, className = '' }) {
    // Inner tile is ~50% of outer so the dashed ring has room to breathe
    const tile = Math.round(size * 0.5);

    return (
        <div
            className={`relative flex items-center justify-center ${className}`}
            style={{ width: size, height: size }}
            data-testid="brain-pulse-loader"
            aria-hidden="true"
        >
            {/* Pulsing emerald glow */}
            <div className="absolute inset-0 rounded-full bg-emerald-400/25 animate-bp-pulse" />

            {/* Rotating dashed sync ring */}
            <svg
                className="absolute inset-0 w-full h-full animate-bp-spin"
                viewBox="0 0 100 100"
                fill="none"
            >
                <circle
                    cx="50"
                    cy="50"
                    r="46"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="6 14"
                    opacity="0.6"
                />
            </svg>

            {/* Brain logo tile - same /icon-192.png used in the
                Dashboard header and the full-screen splash so all
                loading surfaces share one visual identity. */}
            <div
                className="relative rounded-xl overflow-hidden shadow-md animate-bp-float"
                style={{ width: tile, height: tile }}
            >
                <img src="/icon-192.png" alt="" className="w-full h-full object-cover" />
            </div>

            <style>{`
                @keyframes bp-spin { to { transform: rotate(360deg); } }
                @keyframes bp-pulse {
                    0%, 100% { transform: scale(1); opacity: 0.55; }
                    50% { transform: scale(1.18); opacity: 0.25; }
                }
                @keyframes bp-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-3px); }
                }
                .animate-bp-spin { animation: bp-spin 5s linear infinite; }
                .animate-bp-pulse { animation: bp-pulse 2.4s ease-in-out infinite; }
                .animate-bp-float { animation: bp-float 2.4s ease-in-out infinite; }
            `}</style>
        </div>
    );
}
